import { memo, useEffect, useState } from "react";
import Image from "next/image";
import Stars from "src/components/stars/Stars";
import { useTranslations } from "next-intl";
import NewAddToCartForm from "./components/NewAddToCartForm";
import { Image as StorefrontImage, Product } from "src/types/storefront";
import Aos from "aos";
import "aos/dist/aos.css";
import useCountryData from "src/hooks/useCountryData";
import { SupportedPaymentMethod } from "src/types/enums/paymentMethods.enum";
import TabbyPromo from "src/components/tabby/TabbyPromo";
import generatePriceBeforeDiscount from "src/helpers/generatePriceBeforeDiscount";
import NewGallery from "./components/NewGallery";
import Information from "./components/Information";
import classNames from "src/helpers/classNames";
import useAbVariantPrice from "src/hooks/useAbVariantPrice";
import spaceToNbsp from "src/helpers/spaceToNbsp";
import consts from "src/config/consts";
import ProductDescription from "./components/ProductDescription";
import getImagesBySku from "src/helpers/storefront-helpers/getVariantImages";
import isProductInCollection from "src/helpers/storefront-helpers/isProductInCollection";
import { KoalaProduct } from "src/types/koala/KoalaProduct";
import { KoalaVariant } from "src/types/koala/KoalaVariant";
import useRatingsCount from "src/hooks/useRatingsCount";
import usePriceBeforeDiscount from "src/hooks/usePriceBeforeDiscount";
import KoalaAddToCartForm from "./components/KoalaAddToCartForm";

interface KoalaProductOverviewProps {
  product: KoalaProduct;
  variant?: KoalaVariant;
}

const KoalaProductOverview = ({ product, variant }: KoalaProductOverviewProps) => {
  const t = useTranslations("ProductOverview");
  const countryData = useCountryData();
  const ratingsCount = useRatingsCount();
  const [fullScreen, setFullscreen] = useState(false);
  const priceBeforeDiscount = usePriceBeforeDiscount(variant?.price, product.handle);

  /**
   * Effects
   */
  useEffect(() => {
    Aos.init({
      easing: "ease-in",
      once: true,
      disable: "mobile",
    });
  });

  /**
   * Images
   */
  const variantImages = variant ? variant.images : product.images;

  /**
   * currency
   */
  let currency = variant?.currency;
  if (currency === "USD") currency = "$";

  return (
    <div className="lg:grid lg:grid-cols-2 lg:items-start lg:gap-x-4 xl:gap-x-8">
      {/* Image gallery */}
      {/* The fullscreen condition solves the conflict with the header overlaying the image while in fullscreen */}
      <div className={classNames("lg:sticky lg:top-20 ", fullScreen ? "lg:z-50" : "lg:z-10")}>
        {/* Preload all images */}
        {product.images.map((image) => (
          <Image
            className="absolute"
            key={image.url}
            src={image.url}
            alt={""}
            sizes="
        (min-width: 20em) 12rem,
        (min-width: 27em) 21rem,
        (min-width: 64em) 15rem,
        50vw"
            width={0}
            height={0}
          />
        ))}
        <NewGallery
          images={variantImages}
          fullScreen={fullScreen}
          setFullscreen={setFullscreen}
          // withFrenchTechnologyLogo={
          //   !isProductInCollection(product.collections, countryData?.collections.COLLECTION_GIFTS.id)
          // }
        />
      </div>

      <div className="flex flex-col gap-12" data-aos="fade-left">
        <div>
          <div className="flex justify-between py-2.5">
            <div>
              <h1 className="title-1">{product.name}</h1>

              {/* Reviews */}
              {/* {!isProductInCollection(product.collections, countryData?.collections.COLLECTION_GIFTS.id) && ( */}
              <div className="mt-1">
                <h2 className="sr-only">Reviews</h2>
                {/* <div className="items-center flex"> */}
                <div className="items-center sm:flex">
                  <Stars rating={5} starClassName="w-5 h-5" />
                  <div className="text-lg sm:ms-3" suppressHydrationWarning={true}>
                    {t("ratings", { count: ratingsCount })}
                  </div>
                </div>
              </div>
              {/* )} */}
            </div>

            {variant && (
              <div className="max-w-[15rem] sm:max-w-none">
                <div className="flex items-center justify-end gap-2">
                  {/* Generated price */}
                  {priceBeforeDiscount && priceBeforeDiscount > Number(variant.price) ? (
                    <p className="text-xl tracking-tight text-red-500 line-through">
                      {spaceToNbsp(
                        t("price", {
                          amount: priceBeforeDiscount,
                          currency,
                        })
                      )}
                    </p>
                  ) : (
                    <></>
                  )}
                  <p className="text-3xl font-semibold tracking-tight text-gray-900">
                    {spaceToNbsp(
                      t("price", {
                        amount: variant.price,
                        currency,
                      })
                    )}
                  </p>
                </div>

                {priceBeforeDiscount && priceBeforeDiscount > Number(variant.price) && (
                  <div className="flex justify-end">
                    <span className="text-sm font-bold text-red-500">
                      {t("save")}{" "}
                      <span dir="ltr" className="text-2xl">
                        {countryData?.fakeDiscountPerProduct?.[product.handle]}%
                      </span>
                    </span>
                  </div>
                )}

                {/* Tabby Promo */}
                {countryData?.paymentMethods?.includes(SupportedPaymentMethod.tabby) && (
                  <TabbyPromo
                    price={variant.price}
                    currency={currency || ""}
                    // withLineBreak
                  />
                )}
              </div>
            )}
          </div>

          {/* Description */}
          <div className="space-y-4">
            <h3 className="sr-only">Description</h3>
            <div className="space-y-6 text-base text-gray-700" />
            <ProductDescription handle={product.handle} />
          </div>
        </div>

        <div>
          {variant && <KoalaAddToCartForm product={product} variant={variant} stickyFooter={false} />}
          {!variant && <p className="error-message">{t("out_of_stock")}</p>}
        </div>

        {/* productOverviewInfo */}
        {/* {!isProductInCollection(product.collections, countryData?.collections.COLLECTION_GIFTS.id) && (
          <Information product={product} />
        )} */}
        <Information productHandle={product.handle} />
      </div>
    </div>
  );
};

export default memo(KoalaProductOverview);
